import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageSEO from "@/components/feature/PageSEO";

const SHELL_0W40_IMG = "https://public.readdy.ai/ai/img_res/9fecf637-6eb2-4bfe-a475-02152d7eec7c.png";
const SHELL_0W40_B_IMG = "https://storage.readdy-site.link/project_files/0546271a-c60a-4fee-8ef7-76490c0984eb/951470dd-d65a-4d0a-a7cb-255174f39566_-0W-40-4L-.jpg?v=14c8ef3f05833cfdb6434e421bd6244f";
const SHELL_0W40_HK_IMG = "https://public.readdy.ai/ai/img_res/1252ad0c-3993-458b-96ca-1ac5250943c6.png";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

const lubricantsJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Shell Helix Ultra Lubricants Wholesale | HK Channel | Chirunnexa",
    "url": `${SITE_URL}/lubricants`,
    "description": "100% authentic Shell Helix Ultra lubricants wholesale via Hong Kong authorized channels. FCL/LCL fulfillment via Guangzhou and Ningbo ports. B2B pricing for 0W-20, 0W-30, 0W-40, 5W-30, 5W-40.",
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Shell Lubricants Wholesale", "item": `${SITE_URL}/lubricants` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Shell Helix Ultra Lubricants — B2B Wholesale",
    "description": "100% authentic Shell Helix Ultra full synthetic engine oils. Grades: 0W-20, 0W-30, 0W-40, 5W-30, 5W-40. Sourced via Hong Kong authorized distribution channels. Batch-traceable. FCL and LCL wholesale via Guangzhou and Ningbo ports.",
    "url": `${SITE_URL}/lubricants`,
    "brand": { "@type": "Brand", "name": "Shell Helix Ultra" },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "Chirunnexa" },
      "offerCount": "5",
    },
    "additionalProperty": [
      { "@type": "PropertyValue", "name": "Channel", "value": "Hong Kong Authorized Distribution" },
      { "@type": "PropertyValue", "name": "Packaging", "value": "1L, 4L, 208L Drum" },
      { "@type": "PropertyValue", "name": "MOQ", "value": "FCL / LCL" },
    ],
  },
];

const automotiveGrades = [
  {
    grade: "Shell Helix Ultra X 5W-30",
    gradeRu: "Шелл Хеликс Ультра X 5W-30",
    spec: "Full Synthetic · ACEA C3 · API SN · BMW LL-04 · MB 229.51 · VW 504.00/507.00",
    specRu: "Полностью синтетическое · ACEA C3 · API SN · BMW LL-04 · MB 229.51",
    packs: ["1L", "4L", "208L Drum"],
    moq: "FCL / LCL",
    applications: "BMW 3/5/7 Series · Mercedes C/E/S Class · VW Passat · Audi A4/A6",
    highlight: true,
  },
  {
    grade: "Shell Helix Ultra X 5W-40",
    gradeRu: "Шелл Хеликс Ультра X 5W-40",
    spec: "Full Synthetic · ACEA A3/B4 · API SN/CF · MB 229.3/229.5 · Porsche A40",
    specRu: "Полностью синтетическое · ACEA A3/B4 · API SN/CF · MB 229.3/229.5",
    packs: ["1L", "4L", "208L Drum"],
    moq: "FCL / LCL",
    applications: "Mercedes AMG · Porsche Cayenne · BMW M Series · Audi RS",
    highlight: false,
  },
  {
    grade: "Shell Helix Ultra 0W-40",
    gradeRu: "Шелл Хеликс Ультра 0W-40",
    spec: "Full Synthetic · ACEA A3/B3/B4 · API SN · MB 229.3/229.5 · Porsche A40 · VW 502.00/505.00",
    specRu: "Полностью синтетическое · ACEA A3/B3/B4 · API SN · MB 229.3/229.5",
    packs: ["1L", "4L", "208L Drum"],
    moq: "FCL / LCL",
    applications: "Mercedes-Benz · Porsche · VW Group · Jaguar · Land Rover",
    highlight: true,
  },
  {
    grade: "Shell Helix Ultra 0W-30",
    gradeRu: "Шелл Хеликс Ультра 0W-30",
    spec: "Full Synthetic · ACEA C2/C3 · API SN · MB 229.31/229.51 · Renault RN0700/RN0710",
    specRu: "Полностью синтетическое · ACEA C2/C3 · API SN · MB 229.31 · Renault RN0700",
    packs: ["1L", "4L", "208L Drum"],
    moq: "FCL / LCL",
    applications: "Renault · Mercedes · Peugeot · Citroën · Ford EcoBoost",
    highlight: false,
  },
  {
    grade: "Shell Helix Ultra 0W-20",
    gradeRu: "Шелл Хеликс Ультра 0W-20",
    spec: "Full Synthetic · ACEA C5 · API SN Plus · Honda HTO-06 · Ford WSS-M2C948-B",
    specRu: "Полностью синтетическое · ACEA C5 · API SN Plus · Honda HTO-06",
    packs: ["1L", "4L"],
    moq: "FCL / LCL",
    applications: "Honda · Toyota · Ford · Volvo · Jaguar Land Rover (Ingenium)",
    highlight: false,
  },
];

const pricingTiers = [
  {
    tier: "Pilot Order",
    tierRu: "Пробный заказ",
    qty: "1–5 Pallets",
    qtyRu: "1–5 паллет",
    desc: "Quality verification batch. Mixed grades accepted. LCL consolidation available.",
    descRu: "Партия для проверки качества. Допускается смешение марок. Доступна LCL-консолидация.",
    badge: "LCL",
  },
  {
    tier: "Standard B2B",
    tierRu: "Стандартный B2B",
    qty: "1 × 20ft FCL",
    qtyRu: "1 × 20-футовый FCL",
    desc: "Full container load. Single or mixed grades. Standard PO processing. 30-day lead time.",
    descRu: "Полный контейнер. Одна или смешанные марки. Стандартная обработка PO. Срок 30 дней.",
    badge: "FCL",
  },
  {
    tier: "Volume Partner",
    tierRu: "Партнёр по объёму",
    qty: "3+ FCL / Quarter",
    qtyRu: "3+ FCL / квартал",
    desc: "Quarterly framework contracts. Priority allocation. Dedicated account manager. Legally binding.",
    descRu: "Квартальные рамочные контракты. Приоритетное распределение. Персональный менеджер.",
    badge: "PRIORITY",
  },
];

const trustPoints = [
  { icon: "ri-shield-check-line", en: "100% Authentic — HK Channel Traceable", ru: "100% оригинал — прослеживаемость HK" },
  { icon: "ri-file-text-line", en: "Legally Binding International Contracts", ru: "Юридически обязывающие контракты" },
  { icon: "ri-ship-line", en: "FCL & LCL via Guangzhou & Ningbo Ports", ru: "FCL и LCL через Гуанчжоу и Нинбо" },
  { icon: "ri-train-line", en: "China-Europe Railway Express (中欧班列)", ru: "Китайско-европейский ж/д экспресс" },
  { icon: "ri-map-pin-2-line", en: "Guangzhou Port Export Consolidation", ru: "Консолидация в порту Гуанчжоу" },
  { icon: "ri-global-line", en: "15+ Active Export Markets", ru: "15+ активных рынков экспорта" },
];

export default function LubricantsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"en" | "ru">("en");

  return (
    <div style={{ background: "#050d1a", minHeight: "100vh" }}>
      <PageSEO
        title="Shell Helix Ultra Lubricants Wholesale | HK Channel B2B | Chirunnexa"
        description="100% authentic Shell Helix Ultra lubricants wholesale via Hong Kong authorized channels. Grades: 0W-20, 0W-30, 0W-40, 5W-30, 5W-40. Batch-traceable. FCL/LCL via Guangzhou & Ningbo ports. B2B quote within 24h."
        keywords="Shell Helix Ultra wholesale, Shell lubricants B2B, genuine Shell oil Hong Kong channel, automotive lubricants wholesale China, Shell 0W-40 wholesale, Shell 5W-30 wholesale"
        canonical="/lubricants"
        hreflangRu="/ru/lubricants"
        jsonLd={lubricantsJsonLd}
      />
      {/* Back nav */}
      <div
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-16"
        style={{ background: "rgba(5,13,26,0.95)", borderBottom: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(16px)" }}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs font-medium tracking-widest cursor-pointer transition-colors duration-200 whitespace-nowrap"
          style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.14em" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#F59E0B")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.55)")}
        >
          <i className="ri-arrow-left-line" />
          CHIRUNNEXA HUB
        </button>
        <div className="flex items-center gap-2">
          {(["en", "ru"] as const).map((l) => (
            <button
              key={l}
              onClick={() => setActiveTab(l)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap"
              style={{
                background: activeTab === l ? "rgba(245,158,11,0.15)" : "transparent",
                border: activeTab === l ? "1px solid rgba(245,158,11,0.4)" : "1px solid rgba(255,255,255,0.1)",
                color: activeTab === l ? "#F59E0B" : "rgba(255,255,255,0.45)",
              }}
            >
              {l === "en" ? "🌐 EN" : "🇷🇺 RU"}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="relative pt-16" style={{ minHeight: "60vh" }}>
        <div className="absolute inset-0">
          <img
            src="https://storage.readdy-site.link/project_files/0546271a-c60a-4fee-8ef7-76490c0984eb/8ab6b86c-a6bc-4f0b-96f4-d4b2b3f8df5b_2019_04_29_13_36_20_87748B42-7030-4D4F-8C6D-1DA38143A684.jpg?v=82888aabf26fd58d1631007342bd7235"
            alt="Shell Helix Ultra genuine wholesale lubricant warehouse Hong Kong channel"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(5,13,26,0.85) 0%, rgba(5,13,26,0.7) 50%, rgba(5,13,26,1) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24 flex flex-col items-start">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}
          >
            <i className="ri-drop-fill text-xs" style={{ color: "#F59E0B" }} />
            <span className="text-xs tracking-widest uppercase font-medium" style={{ color: "#F59E0B", letterSpacing: "0.2em" }}>
              {activeTab === "en" ? "Strategic Commodity — Automotive Lubricants" : "Стратегический товар — Автомобильные масла"}
            </span>
          </div>
          <h1
            className="font-thin leading-none mb-4 text-white"
            style={{ fontSize: "clamp(2.4rem, 5vw, 4.5rem)", letterSpacing: "0.04em" }}
          >
            {activeTab === "en" ? (
              <>GENUINE SHELL<br /><span style={{ color: "#F59E0B" }}>LUBRICANTS.</span></>
            ) : (
              <>ОРИГИНАЛЬНЫЕ МАСЛА<br /><span style={{ color: "#F59E0B" }}>SHELL.</span></>
            )}
          </h1>
          <p className="text-sm font-light max-w-2xl mt-4" style={{ color: "rgba(255,255,255,0.6)", lineHeight: "1.85" }}>
            {activeTab === "en"
              ? "Direct fulfillment via Hong Kong Channels. 100% authentic Shell Helix Ultra — traceable to authorized Shell distribution networks. Zero grey-market exposure. Volume FCL & LCL fulfillment via Guangzhou Port and Ningbo Port."
              : "Прямые поставки через гонконгские каналы. 100% оригинальные масла Shell Helix Ultra — прослеживаемость до авторизованных дистрибьюторов Shell. Ноль серого рынка. Оптовые FCL и LCL через порты Гуанчжоу и Нинбо."}
          </p>
        </div>
      </div>

      {/* Product Specs Banner Image — 港版 0W-40 4L 满幅展示 */}
      <div className="w-full flex items-center justify-center" style={{ height: "520px", background: "linear-gradient(135deg, #050d1a 0%, #0a1628 50%, #050d1a 100%)" }}>
        <img
          src={SHELL_0W40_HK_IMG}
          alt="Shell Helix Ultra 0W-40 4L Hong Kong channel genuine wholesale B2B"
          className="h-full w-auto object-contain"
          style={{ maxHeight: "520px" }}
        />
      </div>

      {/* Product Grid */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-center gap-4 mb-12">
          <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
          <p className="text-xs tracking-widest uppercase font-semibold" style={{ color: "#F59E0B", letterSpacing: "0.22em" }}>
            {activeTab === "en" ? "Full Product Specifications" : "Полные технические характеристики"}
          </p>
          <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
        </div>

        <div className="flex flex-col gap-4 mb-20">
          {automotiveGrades.map((item) => (
            <div
              key={item.grade}
              className="rounded-2xl p-6 transition-all duration-300 cursor-default"
              style={{
                background: item.highlight ? "rgba(245,158,11,0.06)" : "rgba(255,255,255,0.04)",
                border: item.highlight ? "1px solid rgba(245,158,11,0.3)" : "1px solid rgba(255,255,255,0.08)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(245,158,11,0.4)";
                (e.currentTarget as HTMLDivElement).style.background = "rgba(245,158,11,0.08)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.borderColor = item.highlight ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLDivElement).style.background = item.highlight ? "rgba(245,158,11,0.06)" : "rgba(255,255,255,0.04)";
              }}
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: "#F59E0B" }} />
                    <div>
                      <p className="text-base font-bold text-white mb-0.5">{item.grade}</p>
                      <p className="text-xs font-light" style={{ color: "#F59E0B", fontStyle: "italic" }}>{item.gradeRu}</p>
                    </div>
                  </div>
                  <p className="text-xs font-light mb-1 pl-5" style={{ color: "rgba(255,255,255,0.55)" }}>{item.spec}</p>
                  {activeTab === "ru" && (
                    <p className="text-xs font-light mb-2 pl-5" style={{ color: "rgba(255,255,255,0.35)", fontStyle: "italic" }}>{item.specRu}</p>
                  )}
                  <p className="text-xs font-light pl-5" style={{ color: "rgba(255,255,255,0.35)", fontSize: "10px" }}>
                    <strong style={{ color: "rgba(255,255,255,0.5)" }}>Applications:</strong> {item.applications}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 lg:shrink-0">
                  {item.packs.map((p) => (
                    <span
                      key={p}
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.25)", color: "#F59E0B" }}
                    >
                      {p}
                    </span>
                  ))}
                  <span
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)" }}
                  >
                    {item.moq}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Product image showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20 items-center">
          <div className="relative overflow-hidden rounded-2xl" style={{ height: "380px", background: "#050d1a" }}>
            <img
              src="https://storage.readdy-site.link/project_files/0546271a-c60a-4fee-8ef7-76490c0984eb/1f1c2bf7-518a-42d0-bc61-367aee0de7a0_2022_10_21_11_57_44_AE377740-45A7-487E-B2C0-DD322ED9D7AE.jpg?v=9741d7a7c083b52f9396eedb1999c350"
              alt="Shell Helix Ultra 208L drum barrel wholesale B2B lubricant supply"
              className="absolute inset-0 w-full h-full object-cover object-center scale-105"
            />
          </div>
          <div>
            <p className="text-xs tracking-widest uppercase font-semibold mb-4" style={{ color: "#F59E0B", letterSpacing: "0.2em" }}>
              {activeTab === "en" ? "HK Channel Authenticity" : "Подлинность HK-канала"}
            </p>
            <h3 className="text-2xl font-thin text-white mb-4" style={{ letterSpacing: "0.04em" }}>
              {activeTab === "en" ? "BATCH-TRACEABLE STOCK" : "ПАРТИОННАЯ ПРОСЛЕЖИВАЕМОСТЬ"}
            </h3>
            <div className="mb-4" style={{ width: "40px", height: "2px", background: "#F59E0B" }} />
            <p className="text-sm font-light leading-relaxed" style={{ color: "rgba(255,255,255,0.6)", lineHeight: "1.85" }}>
              {activeTab === "en"
                ? "Every batch of Shell Helix Ultra we supply is traceable to authorized Shell distribution networks via Hong Kong channels. We provide full documentation including batch codes, import certificates, and customs clearance records."
                : "Каждая партия Shell Helix Ultra, которую мы поставляем, прослеживается до авторизованных дистрибьюторских сетей Shell через гонконгские каналы. Мы предоставляем полную документацию: коды партий, импортные сертификаты и таможенные декларации."}
            </p>
          </div>
        </div>

        {/* B2B Pricing Tiers */}
        <div className="mb-20">
          <p className="text-xs tracking-widest uppercase font-semibold mb-8 text-center" style={{ color: "#F59E0B", letterSpacing: "0.22em" }}>
            {activeTab === "en" ? "B2B Wholesale Pricing Tiers" : "Оптовые ценовые уровни B2B"}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {pricingTiers.map((tier) => (
              <div
                key={tier.tier}
                className="rounded-2xl p-7"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold mb-4"
                  style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B", letterSpacing: "0.1em" }}
                >
                  {tier.badge}
                </div>
                <h4 className="text-base font-bold text-white mb-1">{activeTab === "en" ? tier.tier : tier.tierRu}</h4>
                <p className="text-sm font-semibold mb-4" style={{ color: "#F59E0B" }}>
                  {activeTab === "en" ? tier.qty : tier.qtyRu}
                </p>
                <div className="mb-4" style={{ width: "30px", height: "1.5px", background: "#F59E0B" }} />
                <p className="text-xs font-light leading-relaxed" style={{ color: "rgba(255,255,255,0.55)", lineHeight: "1.8" }}>
                  {activeTab === "en" ? tier.desc : tier.descRu}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Points */}
        <div
          className="rounded-2xl p-8 mb-12"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <p className="text-xs tracking-widest uppercase font-semibold mb-6 text-center" style={{ color: "rgba(255,255,255,0.35)", letterSpacing: "0.2em" }}>
            {activeTab === "en" ? "Supply Chain Guarantees" : "Гарантии цепочки поставок"}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trustPoints.map((tp) => (
              <div key={tp.en} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ background: "rgba(245,158,11,0.1)" }}>
                  <i className={`${tp.icon} text-sm`} style={{ color: "#F59E0B" }} />
                </div>
                <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.65)" }}>
                  {activeTab === "en" ? tp.en : tp.ru}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate("/#contact")}
            className="px-10 py-4 rounded-xl text-sm font-bold tracking-widest cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95 text-white whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.14em" }}
          >
            {activeTab === "en" ? "REQUEST B2B QUOTE" : "ЗАПРОСИТЬ ОПТОВОЕ ПРЕДЛОЖЕНИЕ"}
          </button>
          <p className="text-xs mt-3" style={{ color: "rgba(255,255,255,0.3)" }}>
            {activeTab === "en" ? "Response within 24 hours · Standard PO accepted" : "Ответ в течение 24 часов · Принимаем стандартные PO"}
          </p>
        </div>
      </div>
    </div>
  );
}
